/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon',
	'prop-types'
], function(
	React,
	MobxReact,
	BA_UI_CARBON,
	PropTypes
) {
/**
 * @class DropDown
 * @description Creates a property control of type Dropdown.
 * @example
 * {
 * 		item: {
 *			id: 'fontSize', // required field
 *			editor: {
*				uiControl: {
*					className: 'fontSize',
*					readOnly: false,
*					label: 'Font size', 
*					ariaDescribedby: 'Font size',
*					options: [ // required unless getOptions is provided
*						{
*							label: '8px',
*							value: '8'
*						},
*						{
*							label: '9px',
*							value: '9',
*						}
*					],
*					getOptions: getOptionsCallBack // required if options is not provided
*					defaultValue: '8'
*				}
*			},
*			className: 'property--controls property--controls--Dropdown',
*			setPropertyValue: (value) => { console.log(`property value set to ${value}`); }, // required field
*			value: '8'
*		}
*		style: {
*			width: '88px'
*		}
* }
*/

	class DropDown extends React.Component {

		constructor(props) {
			super(props);
			// this key is used to force a render for ba-ui-carbon dropdown				this.state = {};
			// the dropdown maintains the state internally and allows consumers to pass just the initial	
			// state (through the initialSelectedItem param). passing a new key every time the component re-renders,	
			// forces the react reconciliation algorithm to re-evaluate	
			this._dropdownKey = 0;
		}

		render() {
			const { item, style, className } = this.props;
			const { id, setPropertyValue, value } = item;
			if (!id) {
				return null;
			}
			const containerProps = {
				style,
				className,
				'data-property-id': id
			};

			let layoutSpec = item;
			if (item && item.editor && item.editor.uiControl) {
				layoutSpec = item.editor.uiControl;
			}
			let { options, getOptions, ariaDescribedby, readOnly, label, defaultValue } = layoutSpec;
			if (!options || !options.length) {
				if (getOptions && typeof getOptions === 'function') {
					options = getOptions();
				} else {
					return null;
				}
			}
			const defaultSelectedItem = this._getDefaultSelectedItem(value, options, defaultValue);
			const dropDownLabel = label ? label : ariaDescribedby;
			const customClassName = (layoutSpec && layoutSpec.className);
			containerProps.className = containerProps.className ? containerProps.className.concat(customClassName ? ` ${customClassName}` : '') : customClassName;
			return (
				<div {...containerProps}>
					<BA_UI_CARBON.Dropdown
						key={this._dropdownKey++}
						titleText={dropDownLabel}
						items={options}
						size='sm'
						light
						initialSelectedItem={ defaultSelectedItem }
						disabled={readOnly}
						onChange={(event) => {
							setPropertyValue(event.selectedItem.value);
						}}
					/>
				</div>
			);
		}

		_getOptionMatchingValue(options, value) {
			return options.find(option => option.value === value);
		}

		_getDefaultSelectedItem(value, options, defaultValue) {
			let defaultSelectedItem;
			if (value !== undefined) {
				defaultSelectedItem = this._getOptionMatchingValue(options, value);
			}
			if (defaultSelectedItem === undefined && defaultValue !== undefined) {
				defaultSelectedItem = this._getOptionMatchingValue(options, defaultValue);
			}
			if(defaultSelectedItem === undefined){
				// take the default option in the list
				defaultSelectedItem = options.find(option => option.selected === true) || options[0];
			}
			return defaultSelectedItem;
		}
	}

	DropDown.propTypes = {
		item: PropTypes.shape({
			id: PropTypes.string.isRequired,
			ariaDescribedby: PropTypes.string,
			label: PropTypes.string,
			options: PropTypes.array,
			getOptions: PropTypes.func,
			style: PropTypes.string,
			disabled: PropTypes.bool,
			setPropertyValue: PropTypes.func.isRequired,
			value: PropTypes.string,
			editor: PropTypes.object
		}),
		style: PropTypes.object,
		className: PropTypes.string
	};
	DropDown.defaultProps = {};

	return MobxReact.observer(DropDown);
});
